/**
 * The shell: connection, header, and which of the two screens to show.
 */

import { useEffect, useRef } from 'react';

import { type LobbyState, reduceLobby } from '@gok/shared';

import { ConnectionProvider, useChannel, useConnection } from './net/context.tsx';
import { Lobby, SelfBadge } from './routes/Lobby.tsx';
import { MatchView } from './routes/MatchView.tsx';
import { Play } from './routes/Play.tsx';
import { Tutorial } from './routes/Tutorial.tsx';
import { Watch } from './routes/Watch.tsx';
import { matchHref, navigate, useRoute } from './routes/router.ts';
import {
  BotLink,
  ConnectionBadge,
  LearnLink,
  ThemeToggle,
  Wordmark,
} from './ui/Chrome.tsx';

export default function App() {
  return (
    <ConnectionProvider>
      <Shell />
    </ConnectionProvider>
  );
}

function Shell() {
  const route = useRoute();
  const { status } = useConnection();

  return (
    <div className='flex h-full flex-col bg-ground text-ink'>
      <header className='flex shrink-0 items-center gap-3 border-b border-rule bg-surface px-3 py-2'>
        <Wordmark />
        <LearnLink />
        <BotLink />
        <ConnectionBadge status={status} />
        <div className='ml-auto flex items-center gap-2'>
          <SelfBadge />
          <ThemeToggle />
        </div>
      </header>

      {
        /* The lobby is a document and scrolls; the board screens manage their own panes
          and must not. */
      }
      <main
        className={`min-h-0 flex-1 ${
          route.name === 'lobby' ? 'overflow-y-auto' : 'overflow-hidden'
        }`}
      >
        {route.name === 'match' && <MatchView id={route.id} />}
        {route.name === 'learn' && <Tutorial />}
        {route.name === 'play' && <Play />}
        {route.name === 'watch' && <Watch />}
        {route.name === 'lobby' && <Lobby />}
      </main>

      <StartedMatchWatcher />
    </div>
  );
}

/**
 * Take a player to their game when it starts.
 *
 * Whoever accepts a challenge is subscribed to the match by the server, but the player
 * who *posted* it is still sitting in the lobby, and should not have to spot their own
 * game appearing in a list. Only newly started matches count: this must not drag someone
 * back into a game they deliberately navigated away from.
 *
 * The navigation happens in an effect. The 2023 lobby called its router during render,
 * which React explicitly forbids and which produced a redirect loop whenever the lobby
 * updated while a match was live.
 */
function StartedMatchWatcher() {
  const { userId } = useConnection();
  const { state: lobby } = useChannel<LobbyState, never>('lobby', reduceLobby);
  const known = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!lobby || !userId) return;

    // The first snapshot is what already existed; only what appears after it is "new".
    if (known.current === null) {
      known.current = new Set(lobby.live.map((m) => m.id));
      return;
    }

    for (const match of lobby.live) {
      if (known.current.has(match.id)) continue;
      known.current.add(match.id);
      if (match.players.includes(userId)) navigate(matchHref(match.id));
    }
  }, [lobby, userId]);

  return null;
}
